import { createContext, useContext, ReactNode, useState, useRef, useLayoutEffect } from "react";
import { usePlaySound } from "common/utils";

type BuilderSidebarContextValue = {
  collapsed: boolean;
  toggle: () => void;
};

const BuilderSidebarContext = createContext<BuilderSidebarContextValue>({
  collapsed: false,
  toggle: () => undefined,
});

// サイドバーの開閉状態を子（ヘッダー/セクション/ドロップダウン）から参照する
export const useBuilderSidebar = () => useContext(BuilderSidebarContext);

// 開閉トグル。展開時は件数ヘッダー行の右端、折りたたみ時はレール最上部に置く
export const SidebarToggleButton = () => {
  const { collapsed, toggle } = useBuilderSidebar();
  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        toggle();
      }}
      className="type-inherit-font flex-col-center shrink-0 w-10 h-10 border-0 bg-transparent cursor-pointer"
    >
      <span className="material-symbols-outlined type-md text-gray-100">
        {collapsed ? "chevron_right" : "chevron_left"}
      </span>
    </button>
  );
};

// 展開時の件数ヘッダー帯。右端から開閉タブが帯と同一高さで外へ continuous に伸びる。
// タブ高さは inset-y-0 で帯に追従（固定px不使用）。右上は直角にして丸角をタブ外側へ移す。
export const SidebarHeaderBand = ({ children }: { children: ReactNode }) => {
  const { toggle } = useBuilderSidebar();
  return (
    <div
      onClick={toggle}
      className="flex-row-el items-center gap-2 w-full px-3 py-3 rounded-tl-2xl border-0 border-b-2 border-solid border-beige-60 relative cursor-pointer"
    >
      {children}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          toggle();
        }}
        className="type-inherit-font flex-icon-center box-border absolute top-[-2px] right-0 bottom-[-2px] translate-x-full z-20 w-6 bg-white/95 border-2 border-l-0 border-solid border-beige-60 rounded-r-2xl cursor-pointer"
      >
        <span className="material-symbols-outlined type-md text-gray-100">chevron_left</span>
      </button>
    </div>
  );
};

// 一覧の左サイドバー（見出し付きセクションを縦に並べるパネル外枠）。
// 件数ヘッダー行のトグルで「アイコンだけの縦レール」に折りたたみ／展開できる（初期は開いた状態）。
export const BuilderSidebar = ({ children }: { children: ReactNode }) => {
  const play = usePlaySound();
  const [collapsed, setCollapsed] = useState(false);
  // 展開時のパネル高さを記憶し、折りたたみ時も同じ高さを維持する（全高にはしない）
  const panelRef = useRef<HTMLDivElement>(null);
  const [expandedHeight, setExpandedHeight] = useState<number | null>(null);

  useLayoutEffect(() => {
    if (!collapsed && panelRef.current) {
      const h = panelRef.current.offsetHeight;
      setExpandedHeight((prev) => (prev !== h ? h : prev));
    }
  }, [collapsed]);

  const toggle = () => {
    play();
    setCollapsed((v) => !v);
  };

  return (
    <BuilderSidebarContext.Provider value={{ collapsed, toggle }}>
      <aside
        className={`flex-col-el shrink-0 items-start relative h-full pt-4 pointer-events-auto transition-[width] duration-300 ${
          collapsed ? "w-16 px-1 pb-1" : "w-56 px-2 pb-2"
        }`}
      >
        <div
          ref={panelRef}
          style={collapsed && expandedHeight != null ? { height: expandedHeight } : undefined}
          className={`flex-col-el items-start w-full bg-white/95 border-2 border-solid border-beige-60 shadow-md ${
            collapsed ? "rounded-2xl" : "rounded-tl-2xl rounded-bl-2xl rounded-br-2xl"
          }`}
        >
          {children}
        </div>
      </aside>
    </BuilderSidebarContext.Provider>
  );
};
